// Minimum Winning Force solver (SF-89)
// Greedy solver: given target DP, find the smallest composition whose modified OP exceeds it.
// Priority order: prisoners → mercs (if toggled) → soldiers (if toggled) → offense specialists → elites.

import { computeRawOffensePoints } from './militaryMath';

export interface UnitPool {
  offenseSpecialists: number;
  elites: number;
  warHorses: number;
  prisoners: number;
  soldiers: number;
  mercenaries: number;
  // Per-unit offense point values (race-specific)
  offspecOffPoints: number;
  eliteOffPoints: number;
}

export interface AttackMods {
  ome: number;          // offensive military efficiency (wage + buildings + honour + fanatism)
  generals: number;     // 1-4; drives general modifier
  personalityMod: number; // 1.0 normally, 1.15 for Warrior (SF-180: unconditional, not war-gated)
  // Additional multiplicative mods; currently always 1.0 (SF-180: Mage's Fury has zero basis at
  // province-OP level and was removed — see useWarRoomState.ts's buildAttackMods). Kept as a field
  // for a future real province-OP-level modifier, not a dead parameter.
  extraMod: number;
}

export interface ArmyComposition {
  offenseSpecialists: number;
  elites: number;
  warHorses: number;
  prisoners: number;
  soldiers: number;
  mercenaries: number;
}

/**
 * Computes the general bonus (additive to OME).
 * Matches MilitaryMath.computeGeneralModifier.
 */
export function computeGeneralModifier(generals: number): number {
  if (generals === 0) return 0;
  return 0.03 * (generals - 1);
}

/**
 * Computes the full offense multiplier: (OME + generalMod) * personalityMod * extraMod.
 */
export function computeOffenseMultiplier(mods: AttackMods): number {
  const generalMod = computeGeneralModifier(mods.generals);
  return (mods.ome + generalMod) * mods.personalityMod * mods.extraMod;
}

/**
 * Computes modified offense for a given composition.
 * Matches Math.round from MilitaryMath.computeModifiedOffense.
 */
export function computeModifiedOffense(composition: ArmyComposition, pool: UnitPool, mods: AttackMods): number {
  const raw = computeRawOffensePoints(
    composition.offenseSpecialists, pool.offspecOffPoints,
    composition.elites, pool.eliteOffPoints,
    composition.soldiers,
    composition.warHorses,
    composition.prisoners,
    composition.mercenaries
  );
  const multiplier = computeOffenseMultiplier(mods);
  return Math.round(raw * multiplier);
}

/**
 * Find the minimum winning force: smallest composition whose modifiedOP strictly exceeds targetOP
 * (a tie is a loss — see SF-180 Decision 9 below).
 *
 * @param targetOP   The target OP the composition must strictly exceed
 * @param available  At-home unit pool
 * @param includeSoldiers  Whether soldiers are part of the pool
 * @param includeMercenaries  Whether mercenaries are part of the pool
 * @param generals   Number of generals (1-4)
 * @param mods       All offense multipliers
 * @returns ArmyComposition or null if impossible even with full pool
 */
export function computeMinimumWinningForce(
  targetOP: number,
  available: UnitPool,
  includeSoldiers: boolean,
  includeMercenaries: boolean,
  generals: number,
  mods: AttackMods
): ArmyComposition | null {
  const modsMerged: AttackMods = { ...mods, generals };

  // SF-180 Decision 9: SF-89's win condition is strictly modifiedOffense > modifiedDefense — a tie
  // is a loss. Every comparison below uses `>`/`<=` (not `>=`/`<`) to match that exactly, so an
  // Auto-mode composition this solver returns is always a real win, never an exact tie.

  // Feasibility: full pool can't strictly exceed targetOP → impossible.
  const fullComposition: ArmyComposition = {
    offenseSpecialists: available.offenseSpecialists,
    elites: available.elites,
    warHorses: Math.min(available.warHorses, available.offenseSpecialists + available.elites),
    prisoners: available.prisoners,
    soldiers: includeSoldiers ? available.soldiers : 0,
    mercenaries: includeMercenaries ? available.mercenaries : 0,
  };
  if (computeModifiedOffense(fullComposition, available, modsMerged) <= targetOP) {
    return null;
  }

  // Greedy fill: accumulate from priority order until we strictly exceed targetOP.
  // Priority: prisoners → mercs (if toggled) → soldiers (if toggled) → offspec → elite
  const composition: ArmyComposition = {
    offenseSpecialists: 0,
    elites: 0,
    warHorses: 0,
    prisoners: 0,
    soldiers: 0,
    mercenaries: 0,
  };

  // SF-180 Decision 9: fold the paired-war-horse count into every probe itself — not just the
  // final composition after the loop — so the binary search's monotonicity assumption is exact.
  // Harmless no-op for the prisoners/mercs/soldiers steps below (they run before offspec/elites in
  // priority order, so offenseSpecialists+elites is always 0 at that point and this evaluates to 0
  // horses either way).
  const evaluate = (candidate: ArmyComposition): number => {
    const withHorses: ArmyComposition = {
      ...candidate,
      warHorses: Math.min(available.warHorses, candidate.offenseSpecialists + candidate.elites),
    };
    return computeModifiedOffense(withHorses, available, modsMerged);
  };

  const add = (field: keyof ArmyComposition, max: number) => {
    if (evaluate(composition) > targetOP) return;
    let lo = 0;
    let hi = max;
    // Binary search for the minimum count needed
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      const probe = { ...composition, [field]: mid };
      if (evaluate(probe) > targetOP) {
        hi = mid - 1;
      } else {
        lo = mid;
      }
    }
    // lo is the maximum count that still falls short; lo+1 is the minimum that strictly exceeds target
    const needed = Math.min(lo + 1, max);
    composition[field] = needed;
  };

  add('prisoners', available.prisoners);
  if (includeMercenaries) add('mercenaries', available.mercenaries);
  if (includeSoldiers) add('soldiers', available.soldiers);
  add('offenseSpecialists', available.offenseSpecialists);
  add('elites', available.elites);

  // Final war-horse assignment: a provably-exact confirmation of what every probe above already
  // established (via `evaluate()`'s identical pairing rule), not a post-hoc "trim" heuristic.
  composition.warHorses = Math.min(available.warHorses, composition.offenseSpecialists + composition.elites);

  return composition;
}
